import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  projectsApi,
  itemsApi,
  activitiesApi,
  purchaseOrdersApi,
  workersApi,
  filesApi,
  formsApi,
  ramsApi,
  organisationsApi,
  organisationContactsApi,
} from '../services/api';
import { AppContext } from '../App';

// Import tab components
import ProjectDetailsTab from '../components/ProjectDetailsTab';
import ProjectItemsTab from '../components/ProjectItemsTab';
import ProjectActivitiesTab from '../components/ProjectActivitiesTab';
import ProjectWorkersTab from '../components/ProjectWorkersTab';
import ProjectDocumentsTab from '../components/ProjectDocumentsTab';
import ProjectFormsTab from '../components/ProjectFormsTab';
import ProjectPurchaseOrdersTab from '../components/ProjectPurchaseOrdersTab';
import ProjectNotesTab from '../components/ProjectNotesTab';
import ProjectRAMTab from '../components/ProjectRAMTab';
import { calculateEndDate } from '../utils/dateUtils';

const UNSAVED_SURVEY_NOTES_WARNING = 'You have unsaved survey notes. Leave without saving them?';

const getAutoCalculatedDueDate = (startDate, durationInDays, workingSat, workingSun) => {
  const parsedDuration = parseInt(durationInDays, 10);

  if (!startDate || Number.isNaN(parsedDuration) || parsedDuration <= 0) {
    return null;
  }

  return calculateEndDate(startDate, parsedDuration, workingSat, workingSun);
};

const exceedsDueDate = (startDate, dueDate, durationInDays, workingSat, workingSun) => {
  if (!startDate || !dueDate) {
    return false;
  }

  const calculatedDueDate = getAutoCalculatedDueDate(startDate, durationInDays, workingSat, workingSun);

  if (!calculatedDueDate) {
    return false;
  }

  const normalizedDueDate = new Date(dueDate);
  normalizedDueDate.setHours(0, 0, 0, 0);

  return calculatedDueDate > normalizedDueDate;
};

const getDueDateHelperText = (durationInDays, startDate) => {
  if (durationInDays && startDate) {
    return 'Auto-calculated from duration and start date, but can be edited';
  }

  if (durationInDays) {
    return 'Required - will be auto-calculated once a start date is set';
  }

  return 'Required - will be auto-calculated once a duration and start date are set';
};

function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabsRef = React.useRef(null);
  
  // Get current user from App context instead of fetching it again
  const { currentUser } = useContext(AppContext);
  
  const [project, setProject] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [contact, setContact] = useState(null);
  const [items, setItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [files, setFiles] = useState([]);
  const [forms, setForms] = useState([]);
  const [openFormId, setOpenFormId] = useState(null);
  const [originTab, setOriginTab] = useState(null);
  const [returningActivityId, setReturningActivityId] = useState(null);

  // RAMS-related state
  const [rams, setRams] = useState([]);
  const [ramSignatures, setRamSignatures] = useState([]);
  const [ramDataLoading, setRamDataLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyNotesDirty, setSurveyNotesDirty] = useState(false);

  // Track which tabs have been loaded to avoid re-fetching
  const [loadedTabs, setLoadedTabs] = useState(new Set());
  const [tabLoading, setTabLoading] = useState(false);
  const [initialTabSet, setInitialTabSet] = useState(false);

  useEffect(() => {
    if (!surveyNotesDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handleDocumentClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target.closest?.('a[href]');
      if (!link || link.target === '_blank') return;

      const destination = new URL(link.href, window.location.href);
      if (destination.href === window.location.href) return;

      if (!window.confirm(UNSAVED_SURVEY_NOTES_WARNING)) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        setSurveyNotesDirty(false);
      }
    };

    let allowNextPop = false;
    const currentHistoryIndex = window.history.state?.idx;
    const handlePopState = (event) => {
      if (allowNextPop) return;

      if (!window.confirm(UNSAVED_SURVEY_NOTES_WARNING)) {
        event.stopImmediatePropagation();
        const destinationIndex = event.state?.idx;
        const returnDelta = Number.isInteger(currentHistoryIndex) && Number.isInteger(destinationIndex)
          ? currentHistoryIndex - destinationIndex
          : 1;
        allowNextPop = true;
        window.history.go(returnDelta || 1);
      } else {
        setSurveyNotesDirty(false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleDocumentClick, true);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [surveyNotesDirty]);

  // Set initial tab based on user role and RAMS signature requirement
  useEffect(() => {
    console.log('Initial tab setting useEffect triggered');
    console.log('currentUser:', currentUser);
    console.log('project:', project);
    console.log('ramDataLoading:', ramDataLoading);
    console.log('rams.length:', rams.length);
    console.log('ramSignatures.length:', ramSignatures.length);

    if (currentUser && project && !ramDataLoading) {
      const currentVisibleTabs = getVisibleTabs();
      console.log('currentVisibleTabs:', currentVisibleTabs);
      console.log('isRAMSignatureRequired():', isRAMSignatureRequired());

      // For site users on sales order projects, check RAMS signature requirement
      if (isRAMSignatureRequired()) {
        // Find the RAMS tab index in visible tabs and redirect there
        const ramTabIndex = currentVisibleTabs.findIndex(tab => tab.value === 0);
        console.log('ramTabIndex:', ramTabIndex);
        console.log('current activeTab:', activeTab);

        if (ramTabIndex !== -1) {
          console.log('Setting activeTab to RAMS tab:', ramTabIndex);
          setActiveTab(ramTabIndex);
        }
      } else if (activeTab === 0 && !isRAMSignatureRequired()) {
        // Initial load complete - activeTab 0 is now Details tab which needs no extra data
        // Just mark the tab setup as complete without changing tabs
        console.log('Initial tab is Details - no redirect needed');
      }
      
      // Mark that initial tab has been set
      setInitialTabSet(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, project, ramDataLoading, rams, ramSignatures]);
  
  // Handle URL parameters for navigation (e.g., from notifications)
  useEffect(() => {
    const tab = searchParams.get('tab');
    
    if (tab === 'activities' && currentUser && project) {
      // Find the Activities tab index in visible tabs
      const currentVisibleTabs = getVisibleTabs();
      const activitiesTabIndex = currentVisibleTabs.findIndex(t => t.value === 3);
      
      if (activitiesTabIndex !== -1) {
        setActiveTab(activitiesTabIndex);
      }
    }
    if (tab === 'forms' && currentUser && project) {
      const currentVisibleTabs = getVisibleTabs();
      const formsTabIndex = currentVisibleTabs.findIndex(t => t.value === 6);
      const formId = searchParams.get('formId');

      if (formsTabIndex !== -1) {
        setActiveTab(formsTabIndex);
      }
      if (formId) {
        setOpenFormId(parseInt(formId, 10));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser, project]);
  
  // Convert to Sales Order dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertData, setConvertData] = useState({
    customer_reference: '',
    duration_in_days: '',
    due_date: null,
    start_date: null,
    working_sat: false,
    working_sun: false,
    on_site_contact_name: '',
    on_site_contact_phone: '',
  });
  const [convertLoading, setConvertLoading] = useState(false);
  
  // Mark as Completed dialog
  const [completedDialogOpen, setCompletedDialogOpen] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);
  
  // Revert to Sales Order dialog
  const [revertToSalesOrderDialogOpen, setRevertToSalesOrderDialogOpen] = useState(false);
  const [revertToSalesOrderLoading, setRevertToSalesOrderLoading] = useState(false);
  
  // Revert to Quote dialog
  const [revertToQuoteDialogOpen, setRevertToQuoteDialogOpen] = useState(false);
  const [revertToQuoteLoading, setRevertToQuoteLoading] = useState(false);
  
  // Menu for secondary actions
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Define tabs based on user role
  const getAllTabs = () => [
    // Items is the primary first tab for Office users
    { label: 'Quote', value: 1, roles: ['Office'] },
    { label: 'Details', value: 2, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'Activities', value: 3, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'Team', value: 4, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'Documents', value: 5, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'Forms', value: 6, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'RAMS', value: 0, roles: ['Office', 'Site', 'Site Manager'] },
    { label: 'Purchase Orders', value: 7, roles: ['Office', 'Site Manager'] },
    { label: 'Chat', value: 8, roles: ['Office', 'Site', 'Site Manager'] },
  ];  const getVisibleTabs = () => {
    if (!currentUser) return [];
    return getAllTabs().filter(tab => tab.roles.includes(currentUser.role));
  };

  const visibleTabs = getVisibleTabs();
  
  // Fetch all essential data in parallel for maximum performance
  useEffect(() => {
    // Reset state when projectId changes to ensure we don't display old data
    setItems([]);
    setActivities([]);
    setPurchaseOrders([]);
    setWorkers([]);
    setFiles([]);
    setForms([]);
    setOrganisation(null);
    setContact(null);
    setLoadedTabs(new Set());
    setActiveTab(0);
    setInitialTabSet(false);
    setSurveyNotesDirty(false);
    
    const fetchData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        setRamDataLoading(true);
        setError(null);
        
        // Optimize based on user role
        if (currentUser.role === 'Site' || currentUser.role === 'Site Manager') {
          // For Site and Site Manager users, just check RAMS status first (single optimized query)
          // Only fetch full RAMS data if they need to sign RAMS (on RAMS tab)
          const [projectResponse, ramStatusResponse] = await Promise.all([
            projectsApi.getById(projectId),
            ramsApi.getRamStatus(projectId)
          ]);
          
          setProject(projectResponse.data);
          
          // If user hasn't signed all RAMS, we'll need to fetch the full data
          if (!ramStatusResponse.data.all_signed) {
            // Fetch RAMS and signatures for the RAMS tab
            const [ramsResponse, signaturesResponse] = await Promise.all([
              ramsApi.getByProjectId(projectId),
              ramsApi.getSignatures(projectId)
            ]);
            setRams(ramsResponse.data);
            setRamSignatures(signaturesResponse.data);
          } else {
            // All RAMS signed - set empty arrays (data will be lazy loaded if needed)
            setRams([]);
            setRamSignatures([]);
          }
        } else {
          // For Office users, fetch project only on initial load
          // RAMS data will be lazy loaded when RAMS tab is accessed
          const projectResponse = await projectsApi.getById(projectId);
          setProject(projectResponse.data);
          setRams([]);
          setRamSignatures([]);
        }
        
        setLoading(false);
        setRamDataLoading(false);
      } catch (err) {
        console.error('Error fetching project data:', err);
        if (err.response?.status === 403) {
          setError('Access denied. You can only view Sales Order projects you are assigned to.');
        } else {
          setError('Failed to load project data.');
        }
        setLoading(false);
        setRamDataLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, currentUser]);

  // Check if RAMS signature is required to block other tabs
  const isRAMSignatureRequired = () => {
    console.log('isRAMSignatureRequired called');
    console.log('currentUser:', currentUser);
    console.log('currentUser.role:', currentUser?.role);
    console.log('project:', project);
    console.log('project.status:', project?.status);
    console.log('ramDataLoading:', ramDataLoading);
    console.log('rams.length:', rams.length);
    console.log('ramSignatures.length:', ramSignatures.length);

    if (!currentUser || (currentUser.role !== 'Site' && currentUser.role !== 'Site Manager') || !project || project.status !== 'Sales Order') {
      console.log('isRAMSignatureRequired: returning false - user not Site/Site Manager or project not Sales Order');
      return false;
    }

    // If still loading RAMS data, assume signature is required to block access
    if (ramDataLoading) {
      console.log('isRAMSignatureRequired: returning true - still loading RAMS data');
      return true;
    }

    // Check if ALL RAMS have been signed by current user
    if (rams.length === 0) {
      console.log('isRAMSignatureRequired: returning false - no RAMS documents');
      return false; // No RAMs means no signature required
    }

    // Get all signatures by the current user for this project
    const userSignatures = ramSignatures.filter(sig => sig.worker_id === currentUser.worker_id);
    console.log('userSignatures:', userSignatures);
    console.log('userSignatures.length:', userSignatures.length);

    const signedRamIds = userSignatures.map(sig => sig.ram_id);
    console.log('signedRamIds:', signedRamIds);

    // Check if user has signed ALL RAMS documents
    const allRamsSigned = rams.every(ram => signedRamIds.includes(ram.ram_id));
    console.log('allRamsSigned:', allRamsSigned);

    console.log('isRAMSignatureRequired: returning', !allRamsSigned);
    return !allRamsSigned;
  };

  // Lazy loading functions for each tab
  const loadTabData = async (tabValue) => {
    if (loadedTabs.has(tabValue) || !currentUser) return;
    
    setTabLoading(true);
    try {
      switch (tabValue) {
        case 1: // Items tab
          if (currentUser.role === 'Office') {
            const itemsResponse = await itemsApi.getByProjectId(projectId);
            setItems(itemsResponse.data);
            setLoadedTabs(prev => new Set([...prev, tabValue]));
          }
          break;
        
        case 2: // Details tab - load organisation and contact data (Office users only)
          // Only fetch organisation and contact details for Office users
          if (currentUser.role === 'Office') {
            // Fetch organisation details if not already loaded and project has organisation_id
            if (!organisation && project?.organisation_id) {
              try {
                const orgResponse = await organisationsApi.getById(project.organisation_id);
                setOrganisation(orgResponse.data);
              } catch (orgError) {
                console.warn('Could not fetch organisation details:', orgError);
              }
            }
            
            // Fetch contact details if not already loaded and project has organisation_contact_id
            if (!contact && project?.organisation_contact_id) {
              try {
                const contactResponse = await organisationContactsApi.getById(project.organisation_contact_id);
                setContact(contactResponse.data);
              } catch (contactError) {
                console.warn('Could not fetch contact details:', contactError);
              }
            }
          }
          
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        case 3: // Activities tab
          const activitiesResponse = await activitiesApi.getByProjectId(projectId);
          setActivities(activitiesResponse.data);
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        case 4: // Team/Workers tab
          const workersResponse = await workersApi.getByProjectId(projectId);
          setWorkers(workersResponse.data);
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        case 5: { // Documents tab
          const [filesResponse, workersResponse] = await Promise.all([
            filesApi.getByProjectId(projectId),
            workersApi.getByProjectId(projectId)
          ]);
          setFiles(filesResponse.data);
          setWorkers(workersResponse.data);
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        }
        
        case 6: // Forms tab
          const formsResponse = await formsApi.getByProjectId(projectId, null, { summary: true });
          setForms(formsResponse.data);
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        case 7: // Purchase Orders tab
          if (currentUser.role === 'Office' || currentUser.role === 'Site Manager') {
            const purchaseOrdersResponse = await purchaseOrdersApi.getByProjectId(projectId, true);
            setPurchaseOrders(purchaseOrdersResponse.data);
            setLoadedTabs(prev => new Set([...prev, tabValue]));
          }
          break;
        
        case 0: // RAMS tab - fetch data if not already loaded
          if (rams.length === 0 && ramSignatures.length === 0) {
            // RAMS data not loaded yet - fetch it now
            const [ramsResponse, signaturesResponse] = await Promise.all([
              ramsApi.getByProjectId(projectId),
              ramsApi.getSignatures(projectId)
            ]);
            setRams(ramsResponse.data);
            setRamSignatures(signaturesResponse.data);
          }
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        case 8: // Notes tab - data is loaded within the component itself
          setLoadedTabs(prev => new Set([...prev, tabValue]));
          break;
        
        default:
          // Unknown tab value - do nothing
          break;
      }
    } catch (err) {
      console.error(`Error loading tab ${tabValue} data:`, err);
    } finally {
      setTabLoading(false);
    }
  };

  // Load data when tab changes (but only after initial tab is set)
  useEffect(() => {
    if (!currentUser || !project || !initialTabSet) return;
    
    const visibleTab = visibleTabs[activeTab];
    console.log('Lazy loading useEffect triggered:', {
      activeTab,
      visibleTabsLength: visibleTabs.length,
      visibleTab,
      initialTabSet,
      visibleTabs: visibleTabs.map(t => ({ label: t.label, value: t.value }))
    });
    
    if (visibleTab) {
      loadTabData(visibleTab.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUser, project, initialTabSet]);
  
  const handleTabChange = (event, newValue) => {
    console.log('handleTabChange called:', { newValue, currentUser: currentUser?.role, projectStatus: project?.status });
    console.log('isRAMSignatureRequired():', isRAMSignatureRequired());
    console.log('rams length:', rams.length);
    console.log('ramSignatures length:', ramSignatures.length);

    // Check if trying to navigate away from RAMS tab when signature is required
    if (isRAMSignatureRequired()) {
      const currentVisibleTabs = getVisibleTabs();
      const ramTabIndex = currentVisibleTabs.findIndex(tab => tab.value === 0);
      console.log('currentVisibleTabs:', currentVisibleTabs);
      console.log('ramTabIndex:', ramTabIndex);
      console.log('activeTab:', activeTab);

      if (newValue !== ramTabIndex) {
        // Don't allow navigation away from RAMS tab when signature is required
        console.log('Navigation blocked - RAMS signature required');
        return;
      }
    }

    if (surveyNotesDirty && newValue !== activeTab) {
      if (!window.confirm(UNSAVED_SURVEY_NOTES_WARNING)) {
        return;
      }
      setSurveyNotesDirty(false);
    }

    console.log('Navigation allowed, setting activeTab to:', newValue);
    setActiveTab(newValue);
  };

  // After tab change on mobile, gently center the selected pill in view
  useEffect(() => {
    if (!isMobile) return;
    const node = tabsRef.current;
    if (!node) return;
    const selected = node.querySelector('.MuiTab-root.Mui-selected');
    if (selected && typeof selected.scrollIntoView === 'function') {
      selected.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab, isMobile]);
  
  const handleConvertDialogOpen = () => {
    // Pre-populate duration if already set on project
    if (project?.duration_in_days) {
      setConvertData(prevData => ({
        ...prevData,
        duration_in_days: project.duration_in_days.toString(),
      }));
    }
    setConvertDialogOpen(true);
  };
  
  const handleConvertDialogClose = () => {
    setConvertDialogOpen(false);
  };
  
  const handleConvertDataChange = (field, value) => {
    const updatedData = {
      ...convertData,
      [field]: value,
    };
    
    // Auto-calculate due_date when start_date, duration_in_days, or working day options change
    if (field !== 'due_date') {
      const calculatedDueDate = getAutoCalculatedDueDate(
        updatedData.start_date,
        updatedData.duration_in_days,
        updatedData.working_sat,
        updatedData.working_sun
      );

      if (calculatedDueDate) {
        updatedData.due_date = calculatedDueDate;
      }
    }
    
    setConvertData(updatedData);
  };
  
  const handleConvertToSalesOrder = async () => {
    // Check if duration is required (project doesn't have duration set)
    const requiresDuration = !project?.duration_in_days;
    
    // Validate form - start_date is always required
    if (!convertData.due_date || !convertData.customer_reference || !convertData.start_date ||
        (requiresDuration && !convertData.duration_in_days)) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }
    
    try {
      setConvertLoading(true);
      
      // Format dates to ISO string and include duration if provided
      const formattedData = {
        ...convertData,
        due_date: convertData.due_date.toISOString(),
        working_sat: convertData.working_sat,
        working_sun: convertData.working_sun,
        on_site_contact_name: convertData.on_site_contact_name || null,
        on_site_contact_phone: convertData.on_site_contact_phone || null,
      };
      
      // Only include duration if it was provided
      if (convertData.duration_in_days) {
        formattedData.duration_in_days = parseInt(convertData.duration_in_days, 10);
      }
      
      // Format start_date if provided
      if (convertData.start_date) {
        formattedData.start_date = convertData.start_date.toISOString();
      }
      
      // Call API to convert project to sales order
      const response = await projectsApi.convertToSalesOrder(projectId, formattedData);
      
      // Update project with new data
      setProject(response.data.project);
      
      // Update activities if created during conversion
      if (response.data.created_activities && response.data.created_activities.length > 0) {
        const activitiesResponse = await activitiesApi.getByProjectId(projectId);
        setActivities(activitiesResponse.data);
      }
      
      setConvertLoading(false);
      setConvertDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Successfully converted to Sales Order',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error converting to sales order:', err);
      setConvertLoading(false);
      
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to convert to Sales Order',
        severity: 'error',
      });
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const handleOpenLinkedForm = (formId, activityId = null) => {
    const currentVisibleTabs = getVisibleTabs();
    const formsTabIndex = currentVisibleTabs.findIndex(tab => tab.value === 6);
    const resolvedFormId = typeof formId === 'object' ? formId?.project_form_id ?? formId?.id ?? null : formId;

    if (formsTabIndex !== -1 && resolvedFormId != null) {
      setOriginTab(activeTab);
      setReturningActivityId(activityId ?? null);
      setOpenFormId(resolvedFormId);
      setActiveTab(formsTabIndex);
    }
  };

  const handleReturnToOrigin = () => {
    setOpenFormId(null);
    if (originTab !== null) {
      setActiveTab(originTab);
      setOriginTab(null);
    }
  };

  const handleOpenLinkedActivity = (activityId) => {
    const currentVisibleTabs = getVisibleTabs();
    const activitiesTabIndex = currentVisibleTabs.findIndex(tab => tab.value === 3);
    const resolvedActivityId = typeof activityId === 'object'
      ? activityId?.activity_id ?? activityId?.id ?? null
      : activityId;

    if (activitiesTabIndex !== -1 && resolvedActivityId != null) {
      setOriginTab(activeTab);
      setOpenFormId(null);
      setReturningActivityId(resolvedActivityId);
      setActiveTab(activitiesTabIndex);
    }
  };
  
  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    action();
  };
  
  // Mark as Completed handlers
  const handleCompletedDialogOpen = () => {
    setCompletedDialogOpen(true);
  };
  
  const handleCompletedDialogClose = () => {
    setCompletedDialogOpen(false);
  };
  
  const handleMarkAsCompleted = async () => {
    try {
      setCompletedLoading(true);
      
      // Call API to mark project as completed
      const response = await projectsApi.markCompleted(projectId);
      
      // Update project with new data
      setProject(response.data.project);
      
      setCompletedLoading(false);
      setCompletedDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Successfully marked project as completed',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error marking project as completed:', err);
      setCompletedLoading(false);
      
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to mark project as completed',
        severity: 'error',
      });
    }
  };
  
  // Revert to Sales Order handlers
  const handleRevertToSalesOrderDialogOpen = () => {
    setRevertToSalesOrderDialogOpen(true);
  };
  
  const handleRevertToSalesOrderDialogClose = () => {
    setRevertToSalesOrderDialogOpen(false);
  };
  
  const handleRevertToSalesOrder = async () => {
    try {
      setRevertToSalesOrderLoading(true);
      
      // Call API to revert project to sales order
      const response = await projectsApi.revertToSalesOrder(projectId);
      
      // Update project with new data
      setProject(response.data.project);
      
      setRevertToSalesOrderLoading(false);
      setRevertToSalesOrderDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Successfully reverted project to Sales Order',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error reverting project to Sales Order:', err);
      setRevertToSalesOrderLoading(false);
      
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to revert project to Sales Order',
        severity: 'error',
      });
    }
  };
  
  // Revert to Quote handlers
  const handleRevertToQuoteDialogOpen = () => {
    setRevertToQuoteDialogOpen(true);
  };
  
  const handleRevertToQuoteDialogClose = () => {
    setRevertToQuoteDialogOpen(false);
  };
  
  const handleRevertToQuote = async () => {
    try {
      setRevertToQuoteLoading(true);
      
      // Call API to revert project to quote
      const response = await projectsApi.revertToQuote(projectId);
      
      // Update project with new data
      setProject(response.data.project);
      
      setRevertToQuoteLoading(false);
      setRevertToQuoteDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Successfully reverted project to Quote',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error reverting project to Quote:', err);
      setRevertToQuoteLoading(false);
      
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to revert project to Quote',
        severity: 'error',
      });
    }
  };
  
  // Refresh handlers for child components
  const refreshItems = async () => {
    try {
      const response = await itemsApi.getByProjectId(projectId);
      setItems(response.data);
      // Mark as loaded
      setLoadedTabs(prev => new Set([...prev, 1]));
    } catch (err) {
      console.error('Error refreshing items:', err);
    }
  };
  
  const refreshActivities = async () => {
    try {
      const response = await activitiesApi.getByProjectId(projectId);
      setActivities(response.data);
      // Mark as loaded
      setLoadedTabs(prev => new Set([...prev, 3]));
    } catch (err) {
      console.error('Error refreshing activities:', err);
    }
  };
  
  const refreshPurchaseOrders = async () => {
    try {
      const response = await purchaseOrdersApi.getByProjectId(projectId, true);
      setPurchaseOrders(response.data);
      // Mark as loaded
      setLoadedTabs(prev => new Set([...prev, 7]));
    } catch (err) {
      console.error('Error refreshing purchase orders:', err);
    }
  };
  
  const refreshWorkers = async () => {
    try {
      const response = await workersApi.getByProjectId(projectId);
      setWorkers(response.data);
      // Mark as loaded
      setLoadedTabs(prev => new Set([...prev, 4]));
    } catch (err) {
      console.error('Error refreshing workers:', err);
    }
  };
  
  const refreshFiles = async () => {
    try {
      const filesResponse = await filesApi.getByProjectId(projectId);
      setFiles(filesResponse.data);
      
      const formsResponse = await formsApi.getByProjectId(projectId, null, { summary: true });
      setForms(formsResponse.data);
      // Mark as loaded
      setLoadedTabs(prev => new Set([...prev, 5, 6]));
    } catch (err) {
      console.error('Error refreshing files:', err);
    }
  };
  const handleProjectUpdate = (updatedProject) => {
    setProject(updatedProject);
  };

  const handleSurveyNotesDirtyChange = React.useCallback((isDirty) => {
    setSurveyNotesDirty(isDirty);
  }, []);

  const handleSurveyNotesSaved = React.useCallback((notes) => {
    setProject((currentProject) => currentProject
      ? { ...currentProject, survey_notes: notes }
      : currentProject
    );
  }, []);

  const handleProjectDelete = (deletedProjectId) => {
    // Navigate back to projects list after successful deletion
    navigate('/projects');
  };
  
  const getStatusChip = (status) => {
    let color;
    switch (status) {
      case 'Quote':
        color = 'warning';
        break;
      case 'Sales Order':
        color = 'success';
        break;
      case 'Completed':
        color = 'primary';
        break;
      case 'Cancelled':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={status} 
        color={color} 
        size={isMobile ? "small" : "medium"}
        sx={{
          fontSize: { xs: '0.7rem', md: '0.8125rem' },
          height: { xs: 24, md: 32 },
          '& .MuiChip-label': {
            px: { xs: 1, md: 1.5 },
            py: { xs: 0.25, md: 0.5 },
          }
        }}
      />
    );
  };

  // Map visible tab index to actual tab content
  const getTabContent = (visibleTabIndex) => {
    const visibleTab = visibleTabs[visibleTabIndex];
    if (!visibleTab) return null;

    // Show loading spinner if this tab's data is currently being loaded
    if (tabLoading && !loadedTabs.has(visibleTab.value)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (visibleTab.value) {
      case 0: // RAMS
        return (
          <ProjectRAMTab 
            projectId={projectId} 
            currentUser={currentUser} 
            projectStatus={project?.status}
            onSignatureUpdate={() => {
              console.log('onSignatureUpdate callback triggered');
              // Refresh RAMS data when signatures change
              const fetchRAMData = async () => {
                try {
                  const [ramsResponse, signaturesResponse] = await Promise.all([
                    ramsApi.getByProjectId(projectId),
                    ramsApi.getSignatures(projectId)
                  ]);
                  console.log('Refreshed RAMS data after signature update');
                  console.log('ramsResponse.data.length:', ramsResponse.data.length);
                  console.log('signaturesResponse.data.length:', signaturesResponse.data.length);

                  setRams(ramsResponse.data);
                  setRamSignatures(signaturesResponse.data);
                  
                  // Check if user just completed ALL signatures and should be redirected
                  if ((currentUser?.role === 'Site' || currentUser?.role === 'Site Manager') && project?.status === 'Sales Order') {
                    const userSignatures = signaturesResponse.data.filter(sig => sig.worker_id === currentUser.worker_id);
                    const signedRamIds = userSignatures.map(sig => sig.ram_id);
                    const allRamsSigned = signaturesResponse.data.length > 0 && 
                      ramsResponse.data.every(ram => signedRamIds.includes(ram.ram_id));
                    
                    console.log('allRamsSigned check:', allRamsSigned);
                    console.log('ramsResponse.data.length:', ramsResponse.data.length);
                    console.log('userSignatures.length:', userSignatures.length);
                    
                    if (allRamsSigned && ramsResponse.data.length > 0) {
                      console.log('All RAMs signed - redirecting to Items tab');
                      // User has now signed ALL RAMs, redirect to Items tab
                      const currentVisibleTabs = getVisibleTabs();
                      // Redirect to Items tab (now the primary tab, value 1)
                      const itemsTabIndex = currentVisibleTabs.findIndex(tab => tab.value === 1);
                      console.log('itemsTabIndex for redirect:', itemsTabIndex);

                      if (itemsTabIndex !== -1) {
                        setActiveTab(itemsTabIndex);
                      }
                    } else {
                      console.log('Not all RAMS signed yet - staying on RAMS tab');
                    }
                  }
                } catch (err) {
                  console.error('Error refreshing RAMS data:', err);
                }
              };
              fetchRAMData();
            }}
          />
        );
      case 1: // Items (Office only)
        return (
          <ProjectItemsTab 
            projectId={projectId} 
            items={items} 
            projectStatus={project.status}
            project={project}
            refreshItems={refreshItems}
            onSurveyNotesDirtyChange={handleSurveyNotesDirtyChange}
            onSurveyNotesSaved={handleSurveyNotesSaved}
          />
        );
      case 2: // Details
        return <ProjectDetailsTab project={project} organisation={organisation} contact={contact} currentUser={currentUser} onProjectUpdate={handleProjectUpdate} onProjectDelete={handleProjectDelete} />;
      case 3: // Activities
        return (
          <ProjectActivitiesTab 
            projectId={projectId} 
            activities={activities}
            refreshActivities={refreshActivities}
            workers={workers}
            initialActivityId={returningActivityId ?? (searchParams.get('activityId') ? parseInt(searchParams.get('activityId'), 10) : null)}
            onOpenForm={handleOpenLinkedForm}
          />
        );
      case 4: // Workers
        return (
          <ProjectWorkersTab 
            projectId={projectId} 
            workers={workers}
            refreshWorkers={refreshWorkers}
            currentUser={currentUser}
          />
        );
      case 5: // Documents
        return (
          <ProjectDocumentsTab
            projectId={projectId}
            files={files}
            forms={forms}
            refreshFiles={refreshFiles}
            currentUser={currentUser}
            workers={workers}
            onOpenActivity={handleOpenLinkedActivity}
            onOpenForm={handleOpenLinkedForm}
          />
        );
      case 6: // Forms
        return (
          <ProjectFormsTab
            projectId={projectId}
            openFormId={openFormId}
            onOpenFormHandled={() => setOpenFormId(null)}
            onReturnToOrigin={handleReturnToOrigin}
            onOpenActivity={handleOpenLinkedActivity}
          />
        );
      case 7: // Purchase Orders (Office only)
        return (
          <ProjectPurchaseOrdersTab
            projectId={projectId}
            purchaseOrders={purchaseOrders}
            refreshPurchaseOrders={refreshPurchaseOrders}
            projectItems={items}
            project={project}
            currentUser={currentUser}
          />
        );
      case 8: // Notes
        return (
          <ProjectNotesTab
            projectId={projectId}
            projectStatus={project.status}
          />
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper sx={{ p: 3, mt: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }
  
  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper sx={{ p: 3, mt: 3, textAlign: 'center' }}>
          <Typography variant="h6">
            Project not found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/projects')}
            sx={{ mt: 2 }}
          >
            Back to Projects
          </Button>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
        <Box sx={{ 
          mb: { xs: 2, md: 4 }, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontSize: { xs: '1.25rem', md: '2.125rem' },
            fontWeight: { xs: 600, md: 400 },
            lineHeight: 1.3,
            mb: 0.5,
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
            >
          {project.project_no}{project.quote_reference ? ` - ${project.quote_reference}` : ''}
            </Typography>
            <Typography 
          variant="subtitle1" 
          sx={{ 
            fontSize: { xs: '0.875rem', md: '1.125rem' },
            color: 'text.secondary',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
            >
          {organisation?.name || project.organisation_name || 'Unknown Organisation'}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', sm: 'column' }, 
            alignItems: { xs: 'center', sm: 'flex-end' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            gap: 2,
            flexShrink: 0,
          }}>
            {getStatusChip(project.status)}
            {currentUser?.role === 'Office' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {project.status === 'Quote' && (
              <Button
            variant="contained"
            color="primary"
            onClick={handleConvertDialogOpen}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              minWidth: { xs: 'auto', sm: 180 }
            }}
              >
            Convert to Sales Order
              </Button>
            )}
            {project.status === 'Sales Order' && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCompletedDialogOpen}
                  size={isMobile ? "medium" : "large"}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: { xs: 'auto', sm: 180 }
                  }}
                >
                  Mark as Completed
                </Button>
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  sx={{ 
                    border: 1, 
                    borderColor: 'divider',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </>
            )}
            {project.status === 'Purchase Order' && (
              <Button
            variant="contained"
            color="primary"
            onClick={handleCompletedDialogOpen}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              minWidth: { xs: 'auto', sm: 180 }
            }}
              >
            Mark as Completed
              </Button>
            )}
            {project.status === 'Completed' && (
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{ 
                  border: 1, 
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
            )}
          </Box>
        </Box>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {project?.status === 'Sales Order' && (
            <MenuItem onClick={() => handleMenuItemClick(handleRevertToQuoteDialogOpen)}>
              Revert to Quote
            </MenuItem>
          )}
          {project?.status === 'Completed' && (
            <MenuItem onClick={() => handleMenuItemClick(handleRevertToSalesOrderDialogOpen)}>
              Revert to Sales Order
            </MenuItem>
          )}
        </Menu>


      {/* Tabs - mobile optimized */}
      <Paper sx={{ mb: { xs: 2, md: 4 }, overflowX: 'hidden', p: { xs: 0.5, md: 0 } }}>
        <Tabs
          ref={tabsRef}
          value={activeTab}
          onChange={(event, newValue) => {
            console.log('Tabs onChange triggered:', { event, newValue, activeTab });
            handleTabChange(event, newValue);
          }}
          aria-label="project tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scrollButtons': {
              display: { xs: 'none', md: 'flex' },
              width: { md: 40 },
              '&.Mui-disabled': { opacity: 0.3 },
            },
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', md: 120 },
              minHeight: { xs: 40, md: 48 },
              fontSize: { xs: '0.9rem', md: '0.9rem' },
              fontWeight: { xs: 600, md: 500 },
              textTransform: 'none',
              px: { xs: 1.5, md: 2 },
              py: { xs: 0.75, md: 1 },
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              borderRadius: 9999,
              mr: { xs: 1, md: 1 },
              color: 'text.primary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: { xs: 700, md: 600 },
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
              },
            },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0, md: 1 },
              scrollSnapType: { xs: 'x mandatory', md: 'initial' },
            },
            '& .MuiTabs-indicator': {
              display: { xs: 'none', md: 'block' },
              height: { md: 3 },
              borderRadius: '2px 2px 0 0',
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab 
              key={tab.value} 
              label={tab.label} 
              sx={{ maxWidth: { xs: 160, md: 'none' }, scrollSnapAlign: { xs: 'start', md: 'initial' } }}
              onClick={() => console.log('Tab clicked:', { tab, index, activeTab })}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab content */}
  <Box sx={{ minHeight: '400px', overflowX: 'hidden' }}>
        {getTabContent(activeTab)}
      </Box>
      
      {/* Convert to Sales Order Dialog */}
      {currentUser?.role === 'Office' && (
        <Dialog 
          open={convertDialogOpen} 
          onClose={handleConvertDialogClose} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Convert Quote to Sales Order</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Customer Reference *"
                    fullWidth
                    required
                    value={convertData.customer_reference}
                    onChange={(e) => handleConvertDataChange('customer_reference', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={`Duration (in days)${!project?.duration_in_days ? ' *' : ''}`}
                    fullWidth
                    required={!project?.duration_in_days}
                    type="number"
                    value={convertData.duration_in_days}
                    onChange={(e) => handleConvertDataChange('duration_in_days', e.target.value)}
                    InputProps={{ inputProps: { min: 1 } }}
                    helperText={project?.duration_in_days ? "Pre-populated from project, but can be edited" : "Duration is required when converting to Sales Order"}
                    disabled={project?.duration_in_days ? false : false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="On-site Contact Name"
                    fullWidth
                    value={convertData.on_site_contact_name}
                    onChange={(e) => handleConvertDataChange('on_site_contact_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="On-site Contact Phone"
                    fullWidth
                    value={convertData.on_site_contact_phone}
                    onChange={(e) => handleConvertDataChange('on_site_contact_phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={convertData.working_sat}
                          onChange={(e) => handleConvertDataChange('working_sat', e.target.checked)}
                        />
                      }
                      label="Working Saturday"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={convertData.working_sun}
                          onChange={(e) => handleConvertDataChange('working_sun', e.target.checked)}
                        />
                      }
                      label="Working Sunday"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Weekend days are excluded from duration calculation unless checked
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="Start Date *"
                    value={convertData.start_date}
                    onChange={(date) => handleConvertDataChange('start_date', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal',
                        required: true,
                        helperText: 'Required - enter the project start date',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {exceedsDueDate(
                    convertData.start_date,
                    convertData.due_date,
                    convertData.duration_in_days,
                    convertData.working_sat,
                    convertData.working_sun
                  ) ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      With a {convertData.duration_in_days}-day duration starting on this date (and current working days settings), the project cannot be completed by the due date. Please adjust the start date, duration, or due date.
                    </Alert>
                  ) : null}
                  <DatePicker
                    label="Due Date *"
                    value={convertData.due_date}
                    onChange={(date) => handleConvertDataChange('due_date', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        margin: 'normal',
                        error: exceedsDueDate(
                          convertData.start_date,
                          convertData.due_date,
                          convertData.duration_in_days,
                          convertData.working_sat,
                          convertData.working_sun
                        ),
                        helperText: getDueDateHelperText(
                          convertData.duration_in_days,
                          convertData.start_date
                        ),
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
            <Button onClick={handleConvertDialogClose} fullWidth={isMobile}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToSalesOrder} 
              variant="contained" 
              color="primary"
              disabled={convertLoading}
              startIcon={convertLoading && <CircularProgress size={24} />}
              fullWidth={isMobile}
            >
              Convert
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Mark as Completed Dialog */}
      {currentUser?.role === 'Office' && (
        <Dialog 
          open={completedDialogOpen} 
          onClose={handleCompletedDialogClose} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Mark Project as Completed</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to mark this project as completed?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                This will update the project status to "Completed" and record the completion date.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Project: {project.organisation_name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Current Status: {project.status}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
            <Button onClick={handleCompletedDialogClose} fullWidth={isMobile}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkAsCompleted} 
              variant="contained" 
              color="primary"
              disabled={completedLoading}
              startIcon={completedLoading && <CircularProgress size={24} />}
              fullWidth={isMobile}
            >
              Mark as Completed
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Revert to Sales Order Dialog */}
      {currentUser?.role === 'Office' && (
        <Dialog 
          open={revertToSalesOrderDialogOpen} 
          onClose={handleRevertToSalesOrderDialogClose} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Revert Project to Sales Order</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to revert this project back to Sales Order status?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                This will change the project status from "Completed" to "Sales Order" and clear the completion date.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Project: {project.organisation_name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Current Status: {project.status}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
            <Button onClick={handleRevertToSalesOrderDialogClose} fullWidth={isMobile}>
              Cancel
            </Button>
            <Button 
              onClick={handleRevertToSalesOrder} 
              variant="contained" 
              color="warning"
              disabled={revertToSalesOrderLoading}
              startIcon={revertToSalesOrderLoading && <CircularProgress size={24} />}
              fullWidth={isMobile}
            >
              Revert to Sales Order
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Revert to Quote Dialog */}
      {currentUser?.role === 'Office' && (
        <Dialog 
          open={revertToQuoteDialogOpen} 
          onClose={handleRevertToQuoteDialogClose} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Revert Project to Quote</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to revert this project back to Quote status?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                This will change the project status from "Sales Order" to "Quote" and clear sales order specific fields.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Project: {project.organisation_name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Current Status: {project.status}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
            <Button onClick={handleRevertToQuoteDialogClose} fullWidth={isMobile}>
              Cancel
            </Button>
            <Button 
              onClick={handleRevertToQuote} 
              variant="contained" 
              color="warning"
              disabled={revertToQuoteLoading}
              startIcon={revertToQuoteLoading && <CircularProgress size={24} />}
              fullWidth={isMobile}
            >
              Revert to Quote
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ProjectDetail;
